import React from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Typography,
  TypographyProps,
  Modal,
  Stack,
  IconButton,
  Button,
  TextField,
} from '@mui/material';
import _ from 'lodash';
import {
  ClinicResponse,
  GenerativeContentType,
} from 'src/common/types/ClinicResponse';
import { SaveSheet } from 'src/common/types/SaveSheet';

import { ReactComponent as CloseIcon } from '../../../../common/assets/icons/cancel_icon_blue.svg';

type Props = {
  type: GenerativeContentType;
  value: string;
  onClose: () => void;
  currentNote: SaveSheet | null | undefined;
  updateNoteFully: (
    note: SaveSheet,
    showConfirmationMessage: boolean
  ) => Promise<void>;
  clinicResponse: ClinicResponse | null | undefined;
};

const EditGenerativeContentPopup: React.FC<Props & TypographyProps> = (
  props
) => {
  const [isSaving, setIsSaving] = React.useState(false);
  const [editedValue, setEditedValue] = React.useState(props.value);

  const modalStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    border: 'none',
    boxShadow: 24,
    borderRadius: '16px',
    p: 4,
    minWidth: '300px',
    maxWidth: '1200px',
    width: props.type === 'final_code' ? 'auto' : '100%',
    maxHeight: 'calc(100% - 40px)',
    overflowY: 'auto',
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditedValue(event.target.value ?? '');
  };

  React.useEffect(() => {
    setEditedValue(props.value);
  }, [props.value]);

  const title = () => {
    switch (props.type) {
      case 'final_code':
        return 'Final Code';
      case 'cpt_response':
        return 'CPT Codes';
      case 'hcpc_response':
        return 'HCPC Codes';
      case 'icd_response':
        return 'ICD-10 Codes';
      case 'data_response':
        return 'Data Complexity Assessment';
      case 'problem_response':
        return 'Problem Complexity Assessment';
      case 'risk_response':
        return 'Risk Complexity Assessment';
      default:
        break;
    }
  };

  const saveChanges = async () => {
    if (!props.currentNote || !props.type) return;
    setIsSaving(true);

    // Clone deep to avoid having a read only object
    const newNote = _.cloneDeep(props.currentNote);
    if (!newNote.clinicNoteResults) {
      newNote.clinicNoteResults = props.clinicResponse
        ? props.clinicResponse
        : {
            final_code: '',
            cpt_response: '',
            hcpc_response: '',
            icd_response: '',
            data_response: '',
            problem_response: '',
            risk_response: '',
            status_response: '',
            code_details: null,
          };
    }
    newNote.clinicNoteResults[props.type] = editedValue;

    await props.updateNoteFully(newNote, true);
    setIsSaving(false);
    props.onClose();
  };

  return (
    <Modal open={props.type !== null} onClose={() => props.onClose()}>
      <Box sx={modalStyle}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              textAlign='left'
              fontFamily='SFProDisplay-Semibold'
              variant='h6'
            >
              Edit {title()}
            </Typography>
            <IconButton onClick={() => props.onClose()} disabled={isSaving}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box>
            <TextField
              onChange={handleInputChange}
              variant='standard'
              multiline
              fullWidth
              value={editedValue}
              sx={{
                border: 'solid 1px #E0E0E0',
                padding: '4px 8px',
                flex: '1',
              }}
              InputProps={{ disableUnderline: true, spellCheck: false }}
              inputProps={{ style: { color: '#002145B2' } }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              marginTop: '16px',
              justifyContent: 'flex-end',
              width: '100%',
            }}
          >
            <Button
              disabled={isSaving}
              variant='contained'
              color='secondary'
              disableElevation
              sx={{
                marginRight: '16px',
                width: '140px',
                height: 40,
                borderRadius: '15px',
              }}
              onClick={() => props.onClose()}
            >
              <Typography color='white'>Cancel</Typography>
            </Button>
            <LoadingButton
              onClick={() => saveChanges()}
              disabled={editedValue === props.value}
              loading={isSaving}
              variant='contained'
              color='primary'
              disableElevation
              sx={{
                width: '140px',
                height: 40,
                borderRadius: '15px',
              }}
            >
              <Typography color='white'>Save</Typography>
            </LoadingButton>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

export default EditGenerativeContentPopup;
